import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ClipboardJS from 'clipboard';
import {
  formatChipLabel,
  getSourceColor,
  sortExternalIds,
} from '../../../helpers/ExternalId';
import VibeTooltip from '../../VibeTooltip/VibeTooltip';
import './ContentExternalIds.scss';

function ExternalIdChip({
  className = '',
  label,
  source = '',
  extId = '',
  measure = false,
}) {
  const clipboardRef = useRef(null);
  const chipColor = getSourceColor(source);
  const canCopy = !measure && Boolean(extId);

  useEffect(() => {
    if (!canCopy || !clipboardRef.current) {
      return undefined;
    }

    const clipboard = new ClipboardJS(clipboardRef.current);

    return () => {
      clipboard.destroy();
    };
  }, [canCopy]);

  const onClick = (e) => {
    if (!canCopy) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <span
      ref={clipboardRef}
      className={classNames('ExternalIdChip', className, {
        copyable: canCopy,
      })}
      data-external-id-chip={measure ? 'true' : undefined}
      data-clipboard-text={canCopy ? extId : undefined}
      style={{
        '--chip-color': chipColor.color,
        backgroundColor: chipColor.background,
      }}
      onClick={canCopy ? onClick : undefined}
    >
      {label}
    </span>
  );
}

ExternalIdChip.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  source: PropTypes.string,
  extId: PropTypes.string,
  measure: PropTypes.bool,
};

function countChipsThatFit({
  container,
  measure,
}) {
  const chipEls = measure.querySelectorAll('[data-external-id-chip]');
  const overflowEl = measure.querySelector('[data-overflow-chip]');

  if (chipEls.length === 0) {
    return 0;
  }

  const containerWidth = container.clientWidth;
  const styles = window.getComputedStyle(measure);
  const gap = parseFloat(styles.columnGap || styles.gap) || 0;
  const overflowWidth = overflowEl
    ? overflowEl.offsetWidth
    : 0;

  let used = 0;
  let visibleCount = 0;

  for (let index = 0; index < chipEls.length; index += 1) {
    const remaining = chipEls.length - index - 1;
    const chipWidth = chipEls[index].offsetWidth;
    const gapBefore = visibleCount > 0
      ? gap
      : 0;
    const overflowSpace = remaining > 0
      ? gap + overflowWidth
      : 0;

    if (used + gapBefore + chipWidth + overflowSpace > containerWidth) {
      break;
    }

    used += gapBefore + chipWidth;
    visibleCount += 1;
  }

  return visibleCount;
}

function ContentExternalIds({
  className = '',
  externalIds = [],
  filterValues,
}) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const sortedIds = useMemo(
    () => sortExternalIds(externalIds, filterValues),
    [externalIds, filterValues],
  );
  const [visibleCount, setVisibleCount] = useState(sortedIds.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;

    if (!container || !measure) {
      return undefined;
    }

    const updateVisibleCount = () => {
      setVisibleCount(countChipsThatFit({
        container,
        measure,
      }));
    };

    updateVisibleCount();

    const observer = new ResizeObserver(updateVisibleCount);
    observer.observe(container);

    return () => observer.disconnect();
  }, [sortedIds]);

  if (sortedIds.length === 0) {
    return (
      <div className={classNames('ContentExternalIds', className)} />
    );
  }

  const visibleIds = sortedIds.slice(0, visibleCount);
  const overflowCount = sortedIds.length - visibleIds.length;

  const renderChip = (externalId, index, measure = false) => (
    <ExternalIdChip
      key={`external-id-${externalId.source}-${externalId.extId}-${index}`}
      source={externalId.source}
      extId={externalId.extId}
      label={formatChipLabel(externalId)}
      measure={measure}
    />
  );

  return (
    <div
      ref={containerRef}
      className={classNames('ContentExternalIds', className)}
    >
      <div
        ref={measureRef}
        className="external-ids-measure"
        aria-hidden="true"
      >
        {sortedIds.map((externalId, index) => renderChip(externalId, index, true))}
        <span
          className="ExternalIdChip overflow"
          data-overflow-chip="true"
        >
          {`+${sortedIds.length}`}
        </span>
      </div>

      <VibeTooltip
        title={(
          <div className="external-ids-tooltip">
            {sortedIds.map((externalId, index) => renderChip(externalId, `tooltip-${index}`))}
          </div>
        )}
        enterDelay={500}
        placement="top"
      >
        <div className="external-ids-list">
          {visibleIds.map((externalId, index) => renderChip(externalId, index))}
          {overflowCount > 0
            ? (
              <span className="ExternalIdChip overflow">
                {`+${overflowCount}`}
              </span>
            )
            : null}
        </div>
      </VibeTooltip>
    </div>
  );
}

ContentExternalIds.propTypes = {
  className: PropTypes.string,
  externalIds: PropTypes.arrayOf(
    PropTypes.shape({
      source: PropTypes.string,
      extId: PropTypes.string,
    }),
  ),
  filterValues: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

export default ContentExternalIds;
